import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshCache } from '../../../redux/actions/api';
import { logout } from '../../../redux/actions/state';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../../assets/logo.png';
import './style.css';
import Flag from '../../Flag';
import { hasCapability } from '../../../utils/capabilities';

const SidebarMenu = ({ active }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedLocale = useSelector((state) =>
    state.app.locales.find((locale) => locale.language === state.app.selectedLanguage)
  );

  const logoutNow = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <div className="fixed top-0 left-0 bottom-0 bg-orange rounded mt-2 ml-2 mb-2 w-64">
      <div className={'relative'}>
        <img alt="logo" src={logo} className="h-20 w-auto mx-auto mt-2" />
        <span className={'absolute top-0 right-2 text-3xl'}>
          <Flag countryCode={selectedLocale?.countryCode} style={{ width: 20, height: 13 }} />
        </span>
      </div>
      <ul className="overflow-scroll" style={{ height: '100%', paddingBottom: '160px' }}>
        <MenuItem
          title="Content"
          active={
            active === 'home' ||
            active === 'deals' ||
            active === 'tools' ||
            active === 'todo' ||
            active === 'knowledge' ||
            active === 'notification' ||
            active === 'notification-center' ||
            active === 'popup' ||
            active === 'premium-settings' ||
            active === 'settings'
          }
          heading
          content
        />
        <ul className="ml-8 my-1px">
          <MenuItem to="/home" title="Home" active={active === 'home'} content />
          <MenuItem to="/deals" title="Deals" active={active === 'deals'} content />
          <MenuItem to="/tools" title="Tools" active={active === 'tools'} content />
          <MenuItem to="/todo" title="Todo" active={active === 'todo'} content />
          <MenuItem to="/knowledge" title="Knowledge" active={active === 'knowledge'} content />
          <MenuItem
            to="/notification"
            title="Push notifications"
            active={active === 'notification'}
            content
          />
          <MenuItem
            to="/notification-center"
            title="Notification center"
            active={active === 'notification-center'}
            content
          />
          <MenuItem to="/popup" title="Popup" active={active === 'popup'} content />
          <MenuItem
            to="/premium-settings"
            title="Premium settings"
            active={active === 'premium-settings'}
            content
          />
          <MenuItem to="/settings" title="Settings" active={active === 'settings'} content />
        </ul>
        <MenuItem
          title="Analytics"
          active={
            active === 'useranalytics' ||
            active === 'contentanalytics' ||
            active === 'companyanalytics'
          }
          heading
          analytics
        />
        <ul className="ml-8 my-1px">
          <MenuItem
            to="/useranalytics"
            title="Users"
            active={active === 'useranalytics'}
            analytics
          />
          <MenuItem
            to="/active-subscriptions"
            title="Active subscriptions"
            active={active === 'active-subscriptions'}
            analytics
          />
          <MenuItem
            to="/premium-analytics"
            title="Premium Analytics"
            active={active === 'premium-analytics'}
            analytics
          />
          <MenuItem
            to="/contentanalytics"
            title="App content"
            active={active === 'contentanalytics'}
            analytics
          />
          <MenuItem
            to="/companyanalytics"
            title="Company"
            active={active === 'companyanalytics'}
            analytics
          />
        </ul>
        <MenuItem
          to="/capability-grant"
          title="Premium function grant"
          active={active === 'capability-grant'}
          superadminOnly
        />
        <MenuItem to="/forms" title="Forms" active={active === 'forms'} content />
        <MenuItem
          to="/translations"
          title="Translations"
          active={active === 'translations'}
          translation
        />
        <MenuItem to="/help" title="Help" active={active === 'help'} content />
        <MenuItem to="/appversion" title="AppVersion" active={active === 'appversion'} content />
        <MenuItem to="/userdelete" title="Delete user" active={active === 'userdelete'} content />
        <MenuItem title="Logout" active={active === 'b'} onClick={() => logoutNow()} logout />
      </ul>

      <UpdateAppData />
    </div>
  );
};

const MenuItem = ({
  to,
  title,
  active,
  onClick,
  heading,
  logout,
  content,
  analytics,
  translation,
  superadminOnly,
}) => {
  const user = useSelector((state) => state?.auth?.user);
  const { role, adminCapabilities = [] } = user || {};

  const renderMenuItem = () => {
    // Superadmin-only items
    if (superadminOnly) {
      return adminCapabilities.includes('superadmin');
    }

    // Content capability required
    if (content) {
      return role === 'admin' && hasCapability(adminCapabilities, 'content');
    }

    // Analytics capability required
    if (analytics) {
      return role === 'admin' && hasCapability(adminCapabilities, 'analytics');
    }

    // Translation capability required
    if (translation) {
      return role === 'admin' && hasCapability(adminCapabilities, 'translation');
    }

    // Legacy role-based checks (photographer_editor, knowledge_editor, calendar_editor)
    if (
      (role === 'photographer_editor' && to === '/tools') ||
      (role === 'knowledge_editor' && to === '/knowledge') ||
      (role === 'calendar_editor' && to === '/home')
    ) {
      return true;
    }

    // Logout is always visible
    if (logout) {
      return true;
    }

    // Headings without capability requirements
    if (heading && !content && !analytics) {
      return true;
    }

    return false;
  };

  return renderMenuItem() ? (
    <li
      className={`relative z-10 flex flex-row h-7 mx-2 my-1px rounded ${
        active && 'border-2 border-white'
      }`}
    >
      {heading && <div className="text-base text-white ml-2 my-auto ">{title}</div>}
      {to && (
        <Link to={to} title={title} className="text-base text-white ml-2 my-auto">
          {title}
        </Link>
      )}
      {logout && (
        <div
          type="button"
          title={title}
          className="text-base text-white ml-2 my-auto focus:underline cursor:pointer"
          onClick={() => onClick()}
        >
          {title}
        </div>
      )}
    </li>
  ) : (
    <></>
  );
};

export const UpdateAppData = () => {
  const dispatch = useDispatch();

  const updateAppWithNewData = () => {
    dispatch(refreshCache());
  };

  return (
    <div className={'update-app-data'}>
      <button
        type={'button'}
        className={'block mx-auto px-2 rounded h-10 bg-extralightorange'}
        onClick={() => updateAppWithNewData()}
      >
        Update app with new data
      </button>
    </div>
  );
};

export default SidebarMenu;
